<template>
  <div id="dashboard">
    <div class="mb-3">
      <ReportHeader
        title="Dashboard"
        period-filter
        team-filter
        @filterChanged="getData()"
      />
    </div>
    <div class="row">
      <DashboardPanel
        :colour="state.total.colour"
        :count="state.total.count"
        title="Total"
      >
        <SigmaIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state.assigned.colour"
        :count="state.assigned.count"
        title="Assigned"
      >
        <ClockStartIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state['in progress'].colour"
        :count="state['in progress'].count"
        title="In Progress"
      >
        <ClockFastIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state.closed.colour"
        :count="state.closed.count"
        title="Closed"
      >
        <ClockEndIcon title="" />
      </DashboardPanel>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <BaseCard>
          <template #header>
            <font-awesome-icon icon="chart-bar" />
            Ticket By Priority
          </template>
          <div>
            <BarChart
              ref="barChart"
              :chart-data="categoryData"
              :options="categoryOptions"
              :width="null"
              :height="null"
            />
          </div>
        </BaseCard>
      </div>

      <div class="col-lg-6">
        <BaseCard>
          <template #header>
            <font-awesome-icon icon="chart-pie" />
            Ticket Status
          </template>
          <div>
            <DoughnutChart
              :chart-data="statusData"
              :options="statusOptions"
              :width="null"
              :height="null"
            />
          </div>
        </BaseCard>
      </div>
    </div>

    <div class="row">
      <DashboardPanel
        :colour="state.closed.colour"
        :count="percentCompleted + '%'"
        :title="state.closed.count + '/' + state.total.count + ' Closed'"
      >
        <CalendarMultipleCheckIcon title="" />
      </DashboardPanel>
      <DashboardPanel
        :colour="state.cancelled.colour"
        :count="percentCancelled + '%'"
        :title="state.cancelled.count + '/' + state.total.count + ' Cancelled'"
      >
        <ClipboardAlertIcon title="" />
      </DashboardPanel>
      <DashboardPanel
        :colour="state['in progress'].colour"
        :count="percentOpen + '%'"
        :title="state['in progress'].count + '/' + state.total.count + ' In Progress'"
      >
        <CalendarClockIcon title="" />
      </DashboardPanel>
    </div>
  </div>
  <!-- /.content -->
</template>

<script>
/**
 * Main Dashboard view showing projects summaries with charts and graphs.
 */
import moment from 'moment'
import DoughnutChart from '@/views/Report/DoughnutChart.js'
import BarChart from '@/views/Report/BarChart.js'
// import groupAPI from '@/api/group'
import reportAPI from '@/api/report'
import SigmaIcon from 'vue-material-design-icons/Sigma.vue'
import ClockStartIcon from 'vue-material-design-icons/ClockStart.vue'
import ClockFastIcon from 'vue-material-design-icons/ClockFast.vue'
import ClockEndIcon from 'vue-material-design-icons/ClockEnd.vue'
import CalendarMultipleCheckIcon from 'vue-material-design-icons/CalendarMultipleCheck.vue'
import ClipboardAlertIcon from 'vue-material-design-icons/ClipboardAlert.vue'
import CalendarClockIcon from 'vue-material-design-icons/CalendarClock.vue'
import ReportHeader from '@/views/Report/ReportHeader'
import DashboardPanel from '@/views/Report/DashboardPanel'

export default {
  components: {
    BarChart,
    DoughnutChart,
    SigmaIcon,
    ClockStartIcon,
    ClockFastIcon,
    ClockEndIcon,
    CalendarMultipleCheckIcon,
    ClipboardAlertIcon,
    CalendarClockIcon,
    ReportHeader,
    DashboardPanel,
  },
  data () {
    return {
      group: {
        name: 'IP Networks',
      },
      state: {
        total: { count: 0, colour: 'rgb(94,181,225)' },
        assigned: { count: 0, colour: 'rgb(242,146,38)' },
        cancelled: { count: 0, colour: 'rgb(150,121,189)' },
        'in progress': { count: 0, colour: 'rgb(240,178,196)' },
        closed: { count: 0, colour: 'rgb(165,216,87)' },
      },
      withinLabel: 'Within',
      breachedLabel: 'Breached',
      categoryDescr: {},
      defaultColours: [
        '#f39c12',
        '#e6805f',
        '#00a65a',
        '#b04012',
        '#11cc12',
        '#aa5511',
        '#f30012',
        '#ffce56',
        '#1fc8db',
        '#fce473',
        '#42afe3',
        '#ed6c63',
      ],
      categoryColours: {
        'NC1': 'rgb(39,104,169)',
        'EV1': 'rgb(242,212,116)',
        'NV1': 'rgb(121,189,157)',
        'NV2': 'rgb(210,80,80)',
        'O2': 'rgb(165,216,87)',
        'O3': 'rgb(242,146,38)',
        'O4': 'rgb(240,178,196)',
        'O5': 'rgb(150,121,189)',
        'F-Type': 'rgb(206,206,206)',
        'N1': 'rgb(190,220,227)',
      },
      stateColours: {
        'draft': 'rgb(210,80,80)',
        'assigned': 'rgb(242,146,38)',
        'assigned team': 'rgb(206,206,206)',
        'assigned tech lead/pm': 'rgb(242,212,116)',
        'reject1': 'rgb(150,121,189)',
        'quoted': 'rgb(39,104,169)',
        'in progress': 'rgb(240,178,196)',
        'pending closure': 'rgb(121,189,157)',
        'closed': 'rgb(165,216,87)',
      },
      categoryTotal: 0,
      categoryData: {
        labels: [],
        datasets: [],
      },
      categoryOptions: {
        onClick: this.onClickCategory,
        legend: {
          position: 'bottom',
        },
        scales: {
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true,
              // skip non-integer ticks
              callback: function (value) { if (value % 1 === 0) { return value } },
            },
          }],
          xAxes: [{
            stacked: true,
          }],
        },
        tooltips: {
          callbacks: {
            title: this.catTooltipTitle,
            label: this.catTooltipLabel,
          },
        },
      },
      statusData: {
        labels: [],
        datasets: [],
      },
      statusOptions: {
        onClick: this.onClickStatus,
        legend: {
          position: 'bottom',
        },
        tooltips: {
          callbacks: {
            title: this.tooltipTitle,
            label: this.tooltipLabel,
          },
        },
      },
    }
  },
  computed: {
    percentCompleted () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state.closed.count / this.state.total.count * 100)
      }
    },
    percentCancelled () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state.cancelled.count / this.state.total.count * 100)
      }
    },
    percentOpen () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state['in progress'].count / this.state.total.count * 100)
      }
    },
  },
  beforeRouteEnter (to, from, next) {
    if (!to.query || !to.query.startDate) {
      // set default start date to beginning of the year
      const startDate = moment().startOf('year').format('YYYY-MM-DD')
      next({ path: to.path, query: { ...to.query, startDate } })
    } else {
      next()
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (!to.query || !to.query.startDate) {
      // set default start date to beginning of the year
      const startDate = moment().startOf('year').format('YYYY-MM-DD')
      next({ path: to.path, query: { ...to.query, startDate } })
    } else {
      next()
    }
  },
  methods: {
    getData () {
      const { groupId, teamId, startDate, endDate } = this.$route.query
      const gid = groupId || 0

      Promise.all([
        reportAPI.getPropertySummaryReport(gid, teamId, startDate, endDate),
        reportAPI.getStatusSummaryReport(gid, teamId, startDate, endDate),
        // groupAPI.getGroup(gid),
      ]).then(([barStats, pieStats]) => {
        this.setBarStats(barStats.data.priority)
        this.setPieStats(pieStats.data)
        // this.group = group.data
      }).catch(error => {
        console.log(error)
      })
    },
    onClickCategory (event) {
      const elements = this.$refs.barChart._data._chart.getElementAtEvent(event)
      if (!elements || !elements[0]) {
        return
      }
      // add one day to end date so the end day is included
      const endDate = moment(this.$route.query.endDate).add(1, 'days').format('YYYY-MM-DD')
      const element = elements[0]
      const label = element._model.label
      const datasetLabel = element._model.datasetLabel
      const route = {
        name: 'projects',
        query: {
          sla_name: label,
          created_on_gte: this.$route.query.startDate,
          created_on_lt: endDate,
        },
      }
      if (datasetLabel === this.withinLabel) {
        route.query.in_sla = true
      } else if (datasetLabel === this.breachedLabel) {
        route.query.in_sla = false
      }
      this.$router.push(route)
    },
    onClickStatus (event, elements) {
      if (!elements || !elements[0]) {
        return
      }
      const element = elements[0]
      // add one day to end date so the end day is included
      const endDate = moment(this.$route.query.endDate).add(1, 'days').format('YYYY-MM-DD')
      const route = {
        name: 'projects',
        query: {
          // project_type_name: this.group.name,
          project_state_name: element._model.label,
          created_on_gte: this.$route.query.startDate,
          created_on_lt: endDate,
        },
      }
      this.$router.push(route)
    },
    setBarStats (barStats) {
      const labels = []
      const defaultColours = this.defaultColours.slice()
      const data = [
        {
          label: this.breachedLabel,
          backgroundColor: defaultColours.pop(),
          data: [],
        },
        {
          label: this.withinLabel,
          backgroundColor: defaultColours.pop(),
          data: [],
        },
      ]
      this.categoryDescr = {}
      this.categoryTotal = 0
      barStats.forEach(s => {
        labels.push(s.option)
        data[0].data.push(s.count - s.in_sla)
        data[1].data.push(s.in_sla)
        this.categoryDescr[s.option] = s.descr
        this.categoryTotal += s.count
      })
      this.categoryData = {
        labels: labels,
        datasets: data,
      }
    },
    setPieStats (pieStats) {
      const labels = []
      const data = []
      const colours = []
      const defaultColours = this.defaultColours.slice()
      this.state.total.count = 0
      this.state.assigned.count = 0
      this.state.cancelled.count = 0
      this.state['in progress'].count = 0
      this.state.closed.count = 0
      pieStats.forEach(s => {
        this.state.total.count += s.count
        if (s.state === 'reject1' || s.state === 'reject2') {
          this.state.cancelled.count += s.count
        }
        labels.push(s.state)
        data.push(s.count)
        // find colour for state
        s.colour = s.colour || this.stateColours[s.state]
        if (!s.colour) {
          // no assigned colour, allocate one from default colours
          s.colour = defaultColours.pop()
        }
        colours.push(s.colour)
        this.state[s.state] = s
      })
      this.statusData = {
        labels: labels,
        datasets: [{ data: data, backgroundColor: colours }],
      }
    },
    capitalize (s) {
      return s.replace(/\b\w/g, l => l.toUpperCase())
    },
    tooltipTitle (tooltipItems, data) {
      const index = tooltipItems[0].index
      return data.labels[index]
    },
    catTooltipTitle (tooltipItems, data) {
      return tooltipItems[0].label + ' ' + data.datasets[tooltipItems[0].datasetIndex].label
    },
    tooltipLabel (tooltipItem, data) {
      const dataset = data.datasets[tooltipItem.datasetIndex]
      const total = dataset.data.reduce(function (previousValue, currentValue, currentIndex, array) {
        return previousValue + currentValue
      })
      const currentValue = dataset.data[tooltipItem.index]
      const percentage = Math.floor(((currentValue / total) * 100) + 0.5)
      return ' ' + currentValue + ' (' + percentage + '%)'
    },
    catTooltipLabel (tooltipItem, data) {
      const currentValue = Number(tooltipItem.value)
      const percentage = Math.floor(((currentValue / this.categoryTotal) * 100) + 0.5)
      return ' ' + currentValue + ' (' + percentage + '%)'
    },
  },
}
</script>

<style lang="scss" scoped>
#dashboard {
  // fix size and alignment of icons
  .material-design-icon {
    font-size: 48px;
    bottom: 0.125em;
  }
}
</style>
